

// Localização: frontend/src/components/PlanoMedico/PlanoMedicoForm.js (Versão 0.3.0 - Layout Modernizado)

import React, { useState, useEffect } from 'react';
import { User, Pill, Package, Calendar, Clock, FileText, ChevronDown, UserCircle, Syringe, RotateCcw, Box, Repeat, RefreshCw, FlaskConical, NotepadText } from 'lucide-react';
import PrimaryButton from '../Common/PrimaryButton'; 
import LoadingIndicator from '../Common/LoadingIndicator';
import DropdownMenu from '../UI/DropdownMenu';
import { useLotes } from '../../hooks/useLotes';
import { getPacienteFotoUrl } from '../../utils/imageUtils';

// FUNÇÃO AUXILIAR NECESSÁRIA PARA O ESTADO INICIAL
const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return ''; 
        return date.toISOString().split('T')[0];
    } catch (e) {
        return '';
    }
}

// Classe base para os inputs, para manter a consistência visual (atualizado para padrão AgendamentoForm)
const inputClass = "w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 transition";


const PlanoMedicoForm = ({ 
    initialData, 
    onSubmit, 
    onClose, 
    isEditing, 
    medicamentosList = [], 
    pacientesList = [],
    // ❌ REMOVIDO: lotesList não é mais passado como prop
}) => {
    // 💡 1. CHAMA O HOOK PARA GERENCIAR O ESTOQUE
    const { 
        lotesParaPlano, 
        isLoadingLotesPlano, 
        fetchLotesDisponiveisParaPlano 
    } = useLotes();

    // [ ... Lógica de Estado (getInitialFormState) ... ]
    const getInitialFormState = (data) => {
        let initialFrequenciaTipo = 'HOURS';
        let initialFrequenciaValor = '8'; 
        
        if (data?.frequencia_vezes_ao_dia) {
            initialFrequenciaTipo = 'DAILY';
            initialFrequenciaValor = String(data.frequencia_vezes_ao_dia);
        } else if (data?.frequencia_intervalo_horas) {
            if (data.frequencia_intervalo_horas === 24) {
                initialFrequenciaTipo = 'DAILY_ONCE';
            } else {
                initialFrequenciaTipo = 'HOURS';
                initialFrequenciaValor = String(data.frequencia_intervalo_horas);
            }
        }

        const dataHoraInicio = data?.data_inicio && data?.horario_base 
            ? `${data.data_inicio}T${data.horario_base.substring(0, 5)}` 
            : '';

        const isUsoContinuo = !data?.data_fim;

        return {
            paciente_id: data?.paciente_id || '',
            medicamento_id: data?.medicamento_id || '',
            lote_id: data?.lote_id || '',
            dose: data?.dose || '', 
            unidade_medida: data?.unidade_medida || '',
            frequencia_tipo: initialFrequenciaTipo,
            frequencia_valor: initialFrequenciaValor, 
            data_hora_inicio: dataHoraInicio,
            instrucoes_especificas: data?.instrucoes_especificas || '',
            data_fim: isUsoContinuo ? '' : formatDate(data.data_fim), 
            uso_continuo: isUsoContinuo 
        };
    };
    
    const [formData, setFormData] = useState(getInitialFormState(initialData));
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const activePacientesList = pacientesList.filter(paciente => {
        return paciente.status === 'Ativo' || !paciente.status; // Inclui pacientes sem status definido
    });
    
    // 💡 Determina se o paciente é fixo (apenas 1 paciente na lista original OU apenas 1 ativo)
    const isPacienteFixo = pacientesList.length === 1 || activePacientesList.length === 1 || (formData.paciente_id && isEditing);
    
    // 💡 Busca o paciente fixo: primeiro tenta na lista original, depois na lista ativa
    const pacienteFixo = pacientesList.length === 1 ? pacientesList[0] :
                         activePacientesList.length === 1 ? activePacientesList[0] :
                         (formData.paciente_id ? (activePacientesList.find(p => String(p.id) === String(formData.paciente_id)) || pacientesList.find(p => String(p.id) === String(formData.paciente_id))) : null);
    
    // Debug (remover em produção)
    if (process.env.NODE_ENV === 'development') {
        console.log('[PlanoMedicoForm] pacientesList.length:', pacientesList.length);
        console.log('[PlanoMedicoForm] activePacientesList.length:', activePacientesList.length);
        console.log('[PlanoMedicoForm] isPacienteFixo:', isPacienteFixo);
        console.log('[PlanoMedicoForm] pacienteFixo:', pacienteFixo);
        console.log('[PlanoMedicoForm] formData.paciente_id:', formData.paciente_id);
    }
    
    // 💡 EFEITO: Define automaticamente o paciente_id quando há apenas 1 paciente na lista
    useEffect(() => {
        if (activePacientesList.length === 1 && !formData.paciente_id) {
            setFormData(prev => ({
                ...prev,
                paciente_id: String(activePacientesList[0].id)
            }));
        }
    }, [activePacientesList.length, formData.paciente_id]);
    
// 💡 2. EFEITO PARA BUSCAR OS LOTES DINAMICAMENTE (COM DEBUG INICIAL)
    useEffect(() => {
        
        // 🛑 CONVERSÃO E VALIDAÇÃO (mantido)
        const pacienteId = parseInt(formData.paciente_id, 10);
        const medicamentoId = parseInt(formData.medicamento_id, 10);

        // A busca só ocorre se houver um paciente e um medicamento selecionado (como números válidos > 0)
        if (pacienteId > 0 && medicamentoId > 0) {
            console.log(`[FRONTEND DEBUG]: Chamando fetchLotesDisponiveisParaPlano com MedID: ${medicamentoId} e PacienteID: ${pacienteId}`); 
            fetchLotesDisponiveisParaPlano(medicamentoId, pacienteId);
        } else {
            console.log(`[FRONTEND DEBUG]: Condição de busca de lote NÃO atendida. PacID: ${pacienteId}, MedID: ${medicamentoId}`);
        }
    }, [formData.paciente_id, formData.medicamento_id, fetchLotesDisponiveisParaPlano]); // Dependências


    // 💡 3. EFEITO PARA POPULAR DOSE/UNIDADE E RESETAR LOTE (AJUSTADO)
    useEffect(() => {
        const selectedMed = medicamentosList.find(
            (med) => String(med.id) === String(formData.medicamento_id)
        );
        
        let newLoteId = formData.lote_id;
        
        // Se mudou o medicamento E não for o mesmo medicamento da edição original
        const isNewMedOnEdit = isEditing && selectedMed && String(selectedMed.id) !== String(initialData?.medicamento_id);
        const isNewPlan = !isEditing && selectedMed;

        if (isNewPlan || isNewMedOnEdit) {
             // Reseta o lote_id se o medicamento for alterado
            newLoteId = ''; 
        }

        if (selectedMed) {
            setFormData(prev => ({
                ...prev,
                dose: selectedMed.dose || '', 
                unidade_medida: selectedMed.unidade_medida || '',
                lote_id: newLoteId, // Usa o novo lote_id (resetado ou mantido)
            }));
        } else if (!isEditing) {
            setFormData(prev => ({
                ...prev,
                dose: '',
                unidade_medida: '',
                lote_id: '',
            }));
        }
    }, [formData.medicamento_id, medicamentosList, isEditing, initialData]);
    

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        
        if (name === 'uso_continuo') {
            setFormData(prev => ({ 
                ...prev, 
                uso_continuo: checked, 
                data_fim: checked ? '' : (prev.data_fim || formatDate(new Date())) 
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handler para campos que usam DropdownMenu
    const handleDropdownChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setError(null);
    };
    
    // O handleSubmit (Validação e Payload) foi ajustado para manter a validação do lote_id

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // 1. Validações
        if (!formData.paciente_id || !formData.medicamento_id || !formData.dose || !formData.data_hora_inicio) {
            setError('Paciente, Medicamento, Dose e Data/Hora da 1ª Dose são obrigatórios.');
            return;
        }
        //🎯 Validação do Lote
        if (!formData.lote_id){
            setError('O Lote do medicamento é obrigatório para controle de estoque.');
            return;
        }

        if (!formData.uso_continuo && !formData.data_fim) {
            setError('Data de Fim é obrigatória se o uso não for Contínuo.');
            return;
        }
        if (formData.frequencia_tipo !== 'DAILY_ONCE' && parseInt(formData.frequencia_valor, 10) <= 0) {
            setError('O valor de Intervalo/Vezes deve ser maior que zero.');
            return;
        }

        // 2. Desmembrar data_hora_inicio
        const fullDateTime = formData.data_hora_inicio; 
        const [data_inicio, tempo] = fullDateTime.split('T'); 
        const horario_base = tempo ? `${tempo}:00` : ''; 

        // 3. Montar o Payload para o Backend
        const finalPayload = {
            paciente_id: parseInt(formData.paciente_id, 10),
            medicamento_id: parseInt(formData.medicamento_id, 10),
            lote_id: parseInt(formData.lote_id, 10), // ✅ Garantir que é um número
            dose: parseFloat(formData.dose), // ✅ Garantir que é um número
            unidade_medida: formData.unidade_medida,
            frequencia_tipo: 'Recorrente', 
            data_inicio: data_inicio, 
            horario_base: horario_base, 
            instrucoes_especificas: formData.instrucoes_especificas,
            data_fim: formData.uso_continuo ? null : formData.data_fim, 
            frequencia_intervalo_horas: null, 
            frequencia_vezes_ao_dia: null 
        };

        // 4. Mapeamento da Frequência para os campos do BE
        if (formData.frequencia_tipo === 'HOURS') {
            finalPayload.frequencia_intervalo_horas = parseInt(formData.frequencia_valor, 10);
        } else if (formData.frequencia_tipo === 'DAILY') {
            finalPayload.frequencia_vezes_ao_dia = parseInt(formData.frequencia_valor, 10);
        } else if (formData.frequencia_tipo === 'DAILY_ONCE') {
            finalPayload.frequencia_intervalo_horas = 24; 
        }

        setIsSubmitting(true);
        try {
            await onSubmit(finalPayload);
            onClose();
        } catch (submitError) {
            setError(submitError.message || 'Erro ao salvar o Plano de Medicação.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // A variável lotesDisponiveis não é mais necessária, usa-se lotesParaPlano
    
    // Função auxiliar para formatar a data de validade
    const formatValidity = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }


    return (
        <form onSubmit={handleSubmit} className="p-6 bg-white rounded-xl shadow-lg">
            <h3 className="text-2xl font-extrabold mb-6 text-teal-700 border-b pb-2 flex items-center gap-2">
                <NotepadText size={28} className="text-teal-700" />
                {isEditing ? 'Editar Plano de Medicação' : 'Novo Plano de Medicação'}
            </h3>
            
            {error && (
                <div className="p-3 text-sm text-red-700 bg-red-100 rounded mb-4" role="alert">
                    ⚠️ {error}
                </div>
            )}
            
            <div className="space-y-5">
                {/* 1. SELEÇÃO DE PACIENTE - Exibição estática quando fixo, dropdown quando múltiplos */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <User size={16} className="text-gray-500" />
                        Paciente
                    </label>
                    {isPacienteFixo && pacienteFixo ? (
                        // 💡 Exibição estática quando há apenas 1 paciente
                        <div className="w-full flex items-center gap-3 p-3 bg-gray-50">
                            {(() => {
                                const temFoto = pacienteFixo?.fotoURL;
                                const fotoUrl = temFoto ? getPacienteFotoUrl(pacienteFixo.fotoURL) : null;
                                
                                return (
                                    <>
                                        {temFoto && fotoUrl ? (
                                            <img
                                                src={fotoUrl}
                                                alt={pacienteFixo.nome_completo}
                                                className="h-10 w-10 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                                    if (iconElement) iconElement.style.display = 'block';
                                                }}
                                            />
                                        ) : null}
                                        <UserCircle 
                                            size={40} 
                                            className="text-gray-400 fallback-icon" 
                                            style={{ display: (temFoto && fotoUrl) ? 'none' : 'block' }} 
                                        />
                                        <span className="text-gray-700 font-medium text-lg">
                                            {pacienteFixo.nome_completo}
                                        </span>
                                    </>
                                );
                            })()}
                        </div>
                    ) : (
                        // 💡 Dropdown quando há múltiplos pacientes
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    disabled={isEditing}
                                    className={`w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition ${
                                        isEditing ? 'opacity-60 cursor-not-allowed' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const pacienteSelecionado = activePacientesList?.find(p => String(p.id) === String(formData.paciente_id));
                                            
                                            if (!pacienteSelecionado) {
                                                return (
                                                    <>
                                                        <UserCircle size={20} className="text-gray-400" />
                                                        <span className="text-gray-700">Selecione o paciente</span>
                                                    </>
                                                );
                                            }
                                            
                                            const temFoto = pacienteSelecionado?.fotoURL;
                                            const fotoUrl = temFoto ? getPacienteFotoUrl(pacienteSelecionado.fotoURL) : null;
                                            
                                            return (
                                                <>
                                                    {temFoto && fotoUrl ? (
                                                        <img
                                                            src={fotoUrl}
                                                            alt={pacienteSelecionado.nome_completo}
                                                            className="h-6 w-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                                                if (iconElement) iconElement.style.display = 'block';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <UserCircle 
                                                        size={20} 
                                                        className="text-gray-400 fallback-icon" 
                                                        style={{ display: (temFoto && fotoUrl) ? 'none' : 'block' }} 
                                                    />
                                                    <span className="text-gray-700">
                                                        {pacienteSelecionado.nome_completo}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={activePacientesList?.map((p) => {
                                const temFoto = p.fotoURL;
                                const fotoUrl = temFoto ? getPacienteFotoUrl(p.fotoURL) : null;
                                return {
                                    id: String(p.id),
                                    label: p.nome_completo,
                                    ...(temFoto && fotoUrl ? { image: fotoUrl, imageAlt: p.nome_completo } : { icon: UserCircle, iconColor: 'text-gray-400' }),
                                    onClick: () => handleDropdownChange('paciente_id', String(p.id))
                                };
                            }) || []}
                            position="bottom-left"
                            className="w-full"
                        />
                    )}
                </div>

                {/* 2. SELEÇÃO DE MEDICAMENTO - Usando DropdownMenu */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Pill size={16} className="text-gray-500" />
                        Medicamento
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => e.preventDefault()}
                                className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const medicamentoSelecionado = medicamentosList?.find(m => String(m.id) === String(formData.medicamento_id));
                                        
                                        if (!medicamentoSelecionado) {
                                            return (
                                                <>
                                                    <Syringe size={20} className="text-gray-400" />
                                                    <span className="text-gray-700">Selecione o medicamento</span>
                                                </>
                                            );
                                        }
                                        
                                        return (
                                            <>
                                                <FlaskConical size={20} className="text-gray-500" />
                                                <span className="text-gray-700">
                                                    {medicamentoSelecionado.nome_medicamento} ({medicamentoSelecionado.dose || medicamentoSelecionado.dosagem}{medicamentoSelecionado.unidade_medida})
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <ChevronDown size={16} className="text-gray-400" />
                            </button>
                        }
                        items={medicamentosList?.map((med) => ({
                            id: String(med.id),
                            label: `${med.nome_medicamento} (${med.dose || med.dosagem}${med.unidade_medida})`,
                            icon: FlaskConical,
                            iconColor: 'text-gray-600',
                            onClick: () => handleDropdownChange('medicamento_id', String(med.id))
                        })) || []}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* 🎯 SELEÇÃO DE LOTE - Usando DropdownMenu */}
                {formData.medicamento_id && formData.paciente_id && (
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Package size={16} className="text-gray-500" />
                            Lote (Estoque)
                        </label>
                        {isLoadingLotesPlano ? (
                            <div className="flex items-center space-x-2 p-2 border border-gray-300 rounded-md">
                                <LoadingIndicator size="sm" /> 
                                <span className="text-sm text-gray-500">Buscando lotes disponíveis...</span>
                            </div>
                        ) : (
                            <DropdownMenu
                                trigger={
                                    <button
                                        type="button"
                                        onClick={(e) => e.preventDefault()}
                                        className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition"
                                    >
                                        <div className="flex items-center gap-2">
                                            {(() => {
                                                const loteSelecionado = lotesParaPlano?.find(l => String(l.id) === String(formData.lote_id));
                                                
                                                if (!loteSelecionado) {
                                                    return (
                                                        <>
                                                            <Box size={20} className="text-gray-400" />
                                                            <span className="text-gray-700">Selecione o lote</span>
                                                        </>
                                                    );
                                                }
                                                
                                                return (
                                                    <>
                                                        <Box size={20} className="text-gray-500" />
                                                        <span className="text-gray-700">
                                                            {loteSelecionado.lote} | Venc.: {formatValidity(loteSelecionado.data_validade)} | Qtd: {loteSelecionado.quantidade_atual} ({loteSelecionado.tipo_alocacao})
                                                        </span>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <ChevronDown size={16} className="text-gray-400" />
                                    </button>
                                }
                                items={lotesParaPlano?.map((lote) => ({
                                    id: String(lote.id),
                                    label: `${lote.lote} | Venc.: ${formatValidity(lote.data_validade)} | Qtd: ${lote.quantidade_atual} (${lote.tipo_alocacao})`,
                                    icon: Box,
                                    iconColor: 'text-gray-600',
                                    onClick: () => handleDropdownChange('lote_id', String(lote.id))
                                })) || []}
                                position="bottom-left"
                                className="w-full"
                            />
                        )}
                        
                        {!isLoadingLotesPlano && lotesParaPlano.length === 0 && (
                            <p className="text-xs text-red-500 mt-1">
                                ⚠️ Não há lotes ativos ou com estoque útil (alocado/geral) para este medicamento.
                            </p>
                        )}
                    </div>
                )}
                
                {/* 3. DOSE E UNIDADE */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Syringe size={16} className="text-gray-500" />
                            Dose Prescrita
                        </label>
                        <input
                            type="text"
                            name="dose" 
                            value={formData.dose}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="Ex: 2.5, 3, 4"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Pill size={16} className="text-gray-500" />
                            Unidade
                        </label>
                        <input
                            type="text"
                            name="unidade_medida"
                            value={formData.unidade_medida}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="Ex: mg, ml"
                            required
                        />
                    </div>
                </div>
            </div> 

            {/* 4. FREQUÊNCIA E HORÁRIO DE INÍCIO */}
            <h4 className="font-bold text-gray-700 mb-3 mt-6 border-t pt-4 flex items-center gap-2">
                <RotateCcw size={18} className="text-gray-500" />
                Agendamento Recorrente
            </h4>
            <div className="grid grid-cols-3 gap-4 items-start">
                {/* 4.1 Tipo de Frequência - COLUNA 1 */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Clock size={16} className="text-gray-500" />
                        Repetir
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => e.preventDefault()}
                                className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-2">
                                    <Repeat size={16} className="text-gray-500" />
                                    <span className="text-gray-700">
                                        {formData.frequencia_tipo === 'HOURS' ? 'A cada N Horas' : 
                                         formData.frequencia_tipo === 'DAILY' ? 'N Vezes ao dia' : 
                                         'Diário (Horário Fixo)'}
                                    </span>
                                </div>
                                <ChevronDown size={16} className="text-gray-400" />
                            </button>
                        }
                        items={[
                            { id: 'HOURS', label: 'A cada N Horas', icon: RefreshCw, iconColor: 'text-gray-600', onClick: () => handleDropdownChange('frequencia_tipo', 'HOURS') },
                            { id: 'DAILY', label: 'N Vezes ao dia', icon: RefreshCw, iconColor: 'text-gray-600', onClick: () => handleDropdownChange('frequencia_tipo', 'DAILY') },
                            { id: 'DAILY_ONCE', label: 'Diário (Horário Fixo)', icon: RefreshCw, iconColor: 'text-gray-600', onClick: () => handleDropdownChange('frequencia_tipo', 'DAILY_ONCE') }
                        ]}
                        position="bottom-left"
                        className="w-full"
                    />
                </div>

                {/* 4.2 Valor da Frequência (Condicional) - COLUNA 2 */}
                <div className={formData.frequencia_tipo === 'DAILY_ONCE' ? 'opacity-0 pointer-events-none' : ''}>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Calendar size={16} className="text-gray-500" />
                        {formData.frequencia_tipo === 'HOURS' ? 'Intervalo (horas)' : 'Nº de Vezes'}
                    </label>
                    <input
                        type="number"
                        name="frequencia_valor"
                        value={formData.frequencia_valor}
                        onChange={handleChange}
                        className={inputClass}
                        min="1"
                        required={formData.frequencia_tipo !== 'DAILY_ONCE'}
                        disabled={formData.frequencia_tipo === 'DAILY_ONCE'}
                    />
                </div>
                
                {/* 4.3 Data/Hora de Início - COLUNA 3 */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Clock size={16} className="text-gray-500" />
                        Data/Hora da 1ª Dose
                    </label>
                    <input
                        type="datetime-local"
                        name="data_hora_inicio"
                        value={formData.data_hora_inicio}
                        onChange={handleChange}
                        className={inputClass}
                        required
                    />
                </div>
            </div>

            {/* 5. DATA FIM */}
            <div className="mt-6 pt-4 border-t">
                <div className="flex items-center mb-4">
                    <input
                        type="checkbox"
                        name="uso_continuo"
                        id="uso_continuo"
                        checked={formData.uso_continuo}
                        onChange={handleChange}
                        className="h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" 
                    />
                    <label htmlFor="uso_continuo" className="ml-2 block text-gray-600 font-medium flex items-center gap-2">
                        <Calendar size={16} className="text-gray-500" />
                        Uso Contínuo (Sem Data de Fim)
                    </label>
                </div>

                {!formData.uso_continuo && (
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Calendar size={16} className="text-gray-500" />
                            Data de Fim da Prescrição
                        </label>
                        <input
                            type="date"
                            name="data_fim"
                            value={formData.data_fim}
                            onChange={handleChange}
                            className={inputClass}
                            required
                        />
                    </div>
                )}
            </div>

            {/* 6. INSTRUÇÕES ESPECÍFICAS */}
            <div className="mt-4">
                <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                    <FileText size={16} className="text-gray-500" />
                    Instruções Específicas (Ex: com alimento)
                </label>
                <textarea
                    name="instrucoes_especificas"
                    value={formData.instrucoes_especificas || ''}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Descreva instruções específicas para a administração do medicamento..."
                    className={inputClass}
                />
            </div>

            <div className="flex justify-end space-x-3 mt-8 pt-4 border-t">
                <PrimaryButton 
                    type="button" 
                    onClick={onClose} 
                    className="!bg-gray-200 !text-gray-700 hover:!bg-gray-300 shadow-none hover:shadow-md transition-shadow" 
                    disabled={isSubmitting}
                >
                    Cancelar
                </PrimaryButton>
                <PrimaryButton 
                    type="submit" 
                    loading={isSubmitting}
                    variant="primary"
                >
                    {isEditing ? 'Salvar Plano' : 'Criar Plano'}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default PlanoMedicoForm;